import React, { useState, useEffect } from 'react';
import { db } from '@/src/services/db';
import { User, Sector } from '../../types';
import { useAuth } from '@/src/context/AuthContext';
import { 
  User as UserIcon, Shield, Ban, RotateCcw, Search, 
  Calendar, CheckCircle2, AlertTriangle, Plus, X, Lock
} from 'lucide-react';

interface UserManagerProps {
    initialTab?: 'users' | 'logs';
}

const UserManager: React.FC<UserManagerProps> = ({ initialTab = 'users' }) => {
  const { user: adminUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'logs'>(initialTab);
  const [users, setUsers] = useState<User[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals State
  const [showAddUser, setShowAddUser] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  // Form State
  const [newUser, setNewUser] = useState({ name: '', role: 'operator', sectorIds: [] as string[], email: '', password: '' });

  // Edit State
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState('');
  const [editUser, setEditUser] = useState({ name: '', email: '', password: '', role: 'operator', sectorIds: [] as string[], status: 'active' as 'active' | 'inactive' });

  // Reset Password State (Modal Input)
  const [resetUserId, setResetUserId] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const loadData = async () => {
    setLoading(true);
    const u = await db.getUsers();
    const l = await db.getLogs();
    const s = await db.getSectors();
    setUsers(u);
    setLogs(l);
    setSectors(s);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // --- ACTIONS ---

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.name || newUser.sectorIds.length === 0 || !newUser.email || !newUser.password) return alert('Preencha todos os campos e selecione pelo menos um setor');
    if (!adminUser) return alert('Sessão expirada');

    try {
        await db.createUser({
            name: newUser.name,
            role: newUser.role as 'admin' | 'operator',
            sectorIds: newUser.sectorIds,
            email: newUser.email,
            password: newUser.password
        }, adminUser);

        setShowAddUser(false);
        setNewUser({ name: '', role: 'operator', sectorIds: [], email: '', password: '' });
        loadData(); // Refresh list and logs
    } catch (e: any) {
        alert("Erro ao criar usuário: " + e.message);
    }
  };

  const handleEditUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser.name || !editUser.email || editUser.sectorIds.length === 0) return alert('Preencha os campos obrigatórios (Nome, E-mail e Setor)');
    if (editUser.password && editUser.password.length < 6) return alert('A nova senha deve ter pelo menos 6 caracteres');
    if (!adminUser) return alert('Sessão expirada');

    try {
        const payload: any = {
            name: editUser.name,
            email: editUser.email,
            role: editUser.role as 'admin' | 'operator',
            sectorIds: editUser.sectorIds,
            status: editUser.status
        };

        if (editUser.password) {
            payload.password = editUser.password;
        }

        await db.updateUser(editingUserId, payload, adminUser);

        setShowEditUser(false);
        loadData();
    } catch (e: any) {
        alert("Erro ao editar usuário: " + e.message);
    }
  };

  const openEditModal = (user: User) => {
      setEditingUserId(user.id);
      setEditUser({
          name: user.name,
          email: user.email || '',
          password: '', // Blank by default, filled if changing
          role: user.role,
          sectorIds: user.sectorIds || [],
          status: user.status
      });
      setShowEditUser(true);
  };


  const handleToggleStatus = async (userId: string, currentStatus: string, userName: string) => {
      if (!adminUser) return;
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const action = newStatus === 'inactive' ? 'BLOQUEAR' : 'ATIVAR';
      
      if (window.confirm(`Tem certeza que deseja ${action} o acesso para ${userName}?`)) {
          await db.updateUserStatus(userId, newStatus, adminUser);
          loadData();
      }
  };

  const handleChangeRole = async (userId: string, currentRole: string, userName: string) => {
      if (!adminUser) return;
      const newRole = currentRole === 'admin' ? 'operator' : 'admin';
      const label = newRole === 'admin' ? 'ADMINISTRADOR' : 'OPERADOR';

      if (window.confirm(`Deseja alterar o nível de acesso de ${userName} para ${label}?`)) {
          try {
             await db.updateUserRole(userId, newRole, adminUser);
             loadData();
          } catch (e: any) {
              alert("Erro ao alterar papel: " + e.message);
          }
      }
  };

  const openResetModal = (userId: string) => {
      setResetUserId(userId);
      setNewPassword('');
      setTempPassword('');
      setShowResetModal(true);
  };

  const submitResetPassword = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newPassword || newPassword.length < 6) return alert("A senha deve ter pelo menos 6 caracteres");

      try {
          const pwd = await db.resetPassword(resetUserId, newPassword, adminUser);
          setTempPassword(pwd);
          loadData(); // To update logs
      } catch (e: any) {
          alert(e.message);
      }
  };

  // --- FILTERS ---
  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.sector.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
        <div>
            <h2 className="text-2xl font-bold text-slate-800">Administração do Sistema</h2>
            <p className="text-sm text-slate-500">Gerencie controle de acesso e logs de auditoria.</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 flex gap-6">
            <button 
                onClick={() => setActiveTab('users')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'users' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
                Usuários e Acesso
            </button>
            <button 
                onClick={() => setActiveTab('logs')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'logs' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
                Logs de Auditoria
            </button>
        </div>

        {/* --- USERS TAB --- */}
        {activeTab === 'users' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3 sm:items-center justify-between bg-slate-50/50">
                    <div className="relative w-full sm:w-auto">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <input 
                            className="pl-9 py-2.5 bg-white border border-slate-300 rounded-lg text-sm w-full sm:w-64 md:w-80 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-slate-900 placeholder:text-slate-400 shadow-sm" 
                            placeholder="Buscar usuário ou setor..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button 
                        onClick={() => setShowAddUser(true)}
                        className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-indigo-700 flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px] transition-all shadow-sm active:scale-95"
                    >
                        <Plus size={18}/> Novo Usuário
                    </button>
                </div>
                {/* Desktop View: Table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase">
                            <tr>
                                <th className="px-4 py-3">Usuário</th>
                                <th className="px-4 py-3">Função</th>
                                <th className="px-4 py-3">Setor</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-slate-100">
                            {filteredUsers.map((u) => (
                                <tr key={u.id} className="group hover:bg-slate-50 transition-colors">
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-800 flex items-center gap-2">
                                            <div className="w-8 h-8 bg-indigo-50 text-indigo-700 rounded-full flex items-center justify-center text-xs font-bold">
                                                {u.name.charAt(0)}
                                            </div>
                                            {u.name}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 capitalize text-slate-600">
                                        <button
                                            onClick={() => handleChangeRole(u.id, u.role, u.name)}
                                            className="hover:underline hover:text-indigo-600 flex items-center gap-1 decoration-dashed underline-offset-4"
                                            title="Clique para alterar o nível de acesso"
                                        >
                                            {u.role === 'admin' ? 'Administrador' : 'Operador'}
                                            <RotateCcw size={10} className="opacity-0 group-hover:opacity-50 transition-opacity" />
                                        </button>
                                    </td>
                                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(u.sectorIds || []).map(sid => <span key={sid} className="bg-slate-100 px-2 py-0.5 rounded text-xs text-slate-600">{sectors.find(s => s.id === sid)?.name || sid}</span>)}</div></td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {u.status === 'active' ? <CheckCircle2 size={12}/> : <Ban size={12}/>}
                                            {u.status === 'active' ? 'ATIVO' : 'INATIVO'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={() => openResetModal(u.id)}
                                                className="text-slate-400 hover:text-indigo-600 p-2 bg-white border border-slate-200 rounded-lg hover:border-indigo-200 hover:bg-indigo-50 transition-colors shadow-sm" 
                                                title="Redefinir Senha"
                                            >
                                                <RotateCcw size={16}/>
                                            </button>
                                            <button
                                                onClick={() => openEditModal(u)}
                                                className="text-slate-400 hover:text-blue-600 p-2 bg-white border border-slate-200 rounded-lg hover:border-blue-200 hover:bg-blue-50 transition-colors shadow-sm"
                                                title="Editar Usuário"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                                            </button>
                                            <button 
                                                onClick={() => handleToggleStatus(u.id, u.status, u.name)}
                                                className={`p-2 bg-white border border-slate-200 rounded-lg transition-colors shadow-sm ${u.status === 'active' ? 'text-slate-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50' : 'text-green-600 hover:text-green-700 hover:border-green-200 hover:bg-green-50'}`} 
                                                title={u.status === 'active' ? "Bloquear Acesso" : "Restaurar Acesso"}
                                            >
                                                {u.status === 'active' ? <Ban size={16}/> : <CheckCircle2 size={16}/>}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View: Cards */}
                <div className="block md:hidden divide-y divide-slate-100">
                    {filteredUsers.map((u) => (
                        <div key={u.id} className="p-4 flex flex-col gap-4 bg-white hover:bg-slate-50 transition-colors">
                            {/* Card Header: Avatar, Name, and Status */}
                            <div className="flex justify-between items-start gap-3">
                                <div className="flex gap-3 items-center">
                                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex shrink-0 items-center justify-center text-sm text-indigo-700 font-bold">
                                        {u.name.charAt(0)}
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="font-bold text-slate-800 text-base leading-tight">{u.name}</span>
                                        <button
                                            onClick={() => handleChangeRole(u.id, u.role, u.name)}
                                            className="text-left text-xs text-slate-500 font-medium hover:text-indigo-600 flex items-center gap-1 mt-0.5"
                                        >
                                            {u.role === 'admin' ? 'Administrador' : 'Operador'}
                                            <RotateCcw size={10} className="text-slate-300" />
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 shadow-sm ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {u.status === 'active' ? <CheckCircle2 size={12}/> : <Ban size={12}/>}
                                        {u.status === 'active' ? 'ATIVO' : 'INATIVO'}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Card Body: Sectors */}
                            <div className="flex flex-wrap gap-1.5 pl-[52px]">
                                {(u.sectorIds || []).map(sid => (
                                    <span key={sid} className="bg-slate-100 border border-slate-200 px-2 py-1 rounded-md text-[11px] font-medium text-slate-600">
                                        {sectors.find(s => s.id === sid)?.name || sid}
                                    </span>
                                ))}
                            </div>

                            {/* Card Footer: Actions */}
                            <div className="flex justify-end gap-2 pt-3 mt-1 border-t border-slate-100">
                                <button 
                                    onClick={() => openResetModal(u.id)}
                                    className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-slate-600 bg-white border border-slate-200 rounded-xl min-h-[48px] py-2 text-xs sm:text-sm font-semibold hover:bg-slate-50 active:bg-slate-100 transition-colors shadow-sm" 
                                >
                                    <RotateCcw size={18}/> <span className="hidden xs:inline">Senha</span>
                                </button>
                                <button
                                    onClick={() => openEditModal(u)}
                                    className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-xl min-h-[48px] py-2 text-xs sm:text-sm font-semibold hover:bg-blue-100 active:bg-blue-200 transition-colors shadow-sm"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg> <span className="hidden xs:inline">Editar</span>
                                </button>
                                <button 
                                    onClick={() => handleToggleStatus(u.id, u.status, u.name)}
                                    className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 rounded-xl min-h-[48px] py-2 text-xs sm:text-sm font-semibold transition-colors shadow-sm border ${u.status === 'active' ? 'text-red-600 bg-red-50 border-red-200 hover:bg-red-100 active:bg-red-200' : 'text-green-600 bg-green-50 border-green-200 hover:bg-green-100 active:bg-green-200'}`} 
                                >
                                    {u.status === 'active' ? <><Ban size={18}/> <span className="hidden xs:inline">Bloquear</span></> : <><CheckCircle2 size={18}/> <span className="hidden xs:inline">Ativar</span></>}
                                </button>
                            </div>
                        </div>
                    ))}
                    {filteredUsers.length === 0 && <div className="p-8 text-center text-slate-400">Nenhum usuário encontrado.</div>}
                </div>
            </div>
        )}

        {/* --- EDIT USER MODAL --- */}
        {showEditUser && (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-all">
                <div className="bg-white sm:rounded-2xl shadow-2xl w-full max-w-2xl h-[95dvh] sm:h-auto max-h-[100dvh] flex flex-col animate-in slide-in-from-bottom-full sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300 rounded-t-2xl">
                    <div className="p-4 sm:p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50/80 rounded-t-2xl shrink-0">
                        <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2">
                            <UserIcon className="text-indigo-600" />
                            Configurações de Usuário
                        </h2>
                        <button onClick={() => setShowEditUser(false)} className="text-slate-400 hover:text-slate-700 bg-white p-2 rounded-full border border-slate-200 shadow-sm transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                    <div className="overflow-y-auto flex-1 p-4 sm:p-6 bg-white">
                        <form id="edit-user-form" onSubmit={handleEditUserSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Coluna 1: Dados Principais */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1">Nome Completo *</label>
                                        <input
                                            type="text"
                                            required
                                            value={editUser.name}
                                            onChange={e => setEditUser({...editUser, name: e.target.value})}
                                            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                            placeholder="Ex: João da Silva"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1">E-mail *</label>
                                        <input
                                            type="email"
                                            required
                                            value={editUser.email}
                                            onChange={e => setEditUser({...editUser, email: e.target.value})}
                                            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                            placeholder="joao@exemplo.com"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                                            Nova Senha (opcional)
                                        </label>
                                        <input
                                            type="password"
                                            value={editUser.password}
                                            onChange={e => setEditUser({...editUser, password: e.target.value})}
                                            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                            placeholder="Deixe em branco para manter a atual"
                                        />
                                        <p className="text-[11px] text-slate-500 mt-1.5 leading-tight">
                                            Ao preencher, o usuário será forçado a alterar a senha no próximo login.
                                        </p>
                                    </div>
                                </div>

                                {/* Coluna 2: Permissões e Status */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1">Status de Acesso *</label>
                                        <select
                                            required
                                            value={editUser.status}
                                            onChange={e => setEditUser({...editUser, status: e.target.value as 'active'|'inactive'})}
                                            className={`w-full border rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all ${
                                                editUser.status === 'active' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                                            }`}
                                        >
                                            <option value="active">Ativo (Pode fazer login)</option>
                                            <option value="inactive">Inativo (Bloqueado)</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1">Nível de Acesso *</label>
                                        <select
                                            required
                                            value={editUser.role}
                                            onChange={e => setEditUser({...editUser, role: e.target.value as 'admin'|'operator'})}
                                            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                        >
                                            <option value="operator">Operador (Apenas responde)</option>
                                            <option value="admin">Administrador (Acesso total)</option>
                                        </select>
                                    </div>

                                    <div className="flex-1 flex flex-col min-h-0">
                                        <label className="block text-sm font-semibold text-slate-700 mb-1 shrink-0">Setores de Atuação *</label>
                                        <div className="space-y-1 max-h-[30vh] sm:max-h-48 overflow-y-auto p-3 border border-slate-300 rounded-xl bg-slate-50 shadow-inner">
                                            {sectors.map(sector => (
                                                <label key={sector.id} className="flex items-center gap-3 cursor-pointer p-2 hover:bg-slate-100 rounded-lg transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        checked={editUser.sectorIds.includes(sector.id)}
                                                        onChange={(e) => {
                                                            const newSectors = e.target.checked
                                                                ? [...editUser.sectorIds, sector.id]
                                                                : editUser.sectorIds.filter(id => id !== sector.id);
                                                            setEditUser({...editUser, sectorIds: newSectors});
                                                        }}
                                                        className="w-5 h-5 text-indigo-600 focus:ring-indigo-500 rounded border-slate-300"
                                                    />
                                                    <span className="text-sm text-slate-700 font-medium">{sector.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="p-4 sm:p-6 bg-slate-50/50 border-t border-slate-200 shrink-0">
                        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowEditUser(false)}
                                className="w-full sm:w-auto px-6 py-3 min-h-[44px] text-sm font-bold text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 focus:ring-2 focus:ring-indigo-500 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                form="edit-user-form"
                                className="w-full sm:w-auto px-6 py-3 min-h-[44px] text-sm font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 shadow-md focus:ring-2 focus:ring-indigo-500 transition-colors"
                            >
                                Salvar Alterações
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* --- ADD USER MODAL --- */}
        {showAddUser && (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-all">
                <div className="bg-white sm:rounded-2xl shadow-2xl w-full max-w-md h-[95dvh] sm:h-auto max-h-[100dvh] flex flex-col animate-in slide-in-from-bottom-full sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300 rounded-t-2xl">
                    <div className="p-4 sm:p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50/80 rounded-t-2xl shrink-0">
                        <h3 className="font-bold text-lg text-slate-800">Criar Novo Usuário</h3>
                        <button onClick={() => setShowAddUser(false)} className="text-slate-400 hover:text-slate-700 bg-white p-2 rounded-full border border-slate-200 shadow-sm transition-colors">
                            <X size={20}/>
                        </button>
                    </div>
                    <div className="overflow-y-auto flex-1 p-4 sm:p-6">
                        <form id="add-user-form" onSubmit={handleCreateUser} className="space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Nome Completo</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none shadow-sm transition-all" 
                                    placeholder="ex: João Silva"
                                    value={newUser.name}
                                    onChange={e => setNewUser({...newUser, name: e.target.value})}
                                    autoFocus
                                />
                            </div>

                            {/* AUTH FIELDS */}
                             <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">E-mail de Acesso</label>
                                <input
                                    type="email"
                                    className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none shadow-sm transition-all"
                                    placeholder="ex: joao@acme.com"
                                    value={newUser.email}
                                    onChange={e => setNewUser({...newUser, email: e.target.value})}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Senha Inicial</label>
                                <input
                                    type="password"
                                    className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none shadow-sm transition-all"
                                    placeholder="******"
                                    value={newUser.password}
                                    onChange={e => setNewUser({...newUser, password: e.target.value})}
                                    required
                                    minLength={6}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Função</label>
                                    <select 
                                        className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm transition-all"
                                        value={newUser.role}
                                        onChange={e => setNewUser({...newUser, role: e.target.value})}
                                    >
                                        <option value="operator">Operador</option>
                                        <option value="admin">Administrador</option>
                                    </select>
                                </div>
                                <div className="flex flex-col min-h-0">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Setores de Atuação *</label>
                                    <div className="space-y-1 max-h-[20vh] sm:max-h-32 overflow-y-auto p-3 border border-slate-300 rounded-xl bg-slate-50 shadow-inner">
                                        {sectors.map(sector => (
                                            <label key={`new-${sector.id}`} className="flex items-center gap-3 cursor-pointer p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={newUser.sectorIds.includes(sector.id)}
                                                    onChange={(e) => {
                                                        const newSectors = e.target.checked
                                                            ? [...newUser.sectorIds, sector.id]
                                                            : newUser.sectorIds.filter(id => id !== sector.id);
                                                        setNewUser({...newUser, sectorIds: newSectors});
                                                    }}
                                                    className="w-5 h-5 text-indigo-600 focus:ring-indigo-500 rounded border-slate-300"
                                                />
                                                <span className="text-sm text-slate-700 font-medium">{sector.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="p-4 sm:p-6 bg-slate-50/50 border-t border-slate-200 shrink-0">
                        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                            <button 
                                type="button" 
                                onClick={() => setShowAddUser(false)} 
                                className="w-full sm:w-auto px-6 py-3 min-h-[44px] bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-xl text-sm font-bold transition-colors"
                            >
                                Cancelar
                            </button>
                            <button 
                                type="submit" 
                                form="add-user-form"
                                className="w-full sm:w-auto px-6 py-3 min-h-[44px] bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 shadow-md transition-colors"
                            >
                                Criar Usuário
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* --- PASSWORD RESET MODAL --- */}
        {showResetModal && (
            <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
                 <div className="bg-white rounded-xl shadow-xl w-full max-w-sm animate-in zoom-in-95 duration-200 text-center p-8">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto text-green-600 mb-4">
                        <Lock size={24} />
                    </div>
                    <h3 className="font-bold text-xl text-slate-800 mb-2">Redefinição de Senha com Sucesso</h3>
                    <p className="text-sm text-slate-500 mb-6">
                        Compartilhe esta senha temporária com o usuário imediatamente. Ela não será mostrada novamente.
                    </p>
                    
                    <div className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-4 mb-6">
                        <span className="font-mono text-2xl font-bold text-slate-800 tracking-wider select-all">
                            {tempPassword}
                        </span>
                    </div>

                    <button 
                        onClick={() => setShowResetModal(false)}
                        className="w-full bg-slate-900 text-white py-3 rounded-lg font-bold hover:bg-slate-800"
                    >
                        Fechar e Copiar
                    </button>
                 </div>
            </div>
        )}
    </div>
  );
};

export default UserManager;
